import { XMarkIcon } from '@heroicons/react/24/outline';
import { RotateLeft as RotateLeftIcon } from '@praetorian-chariot/ui';
import { DownloadIcon } from 'lucide-react';

import type { MenuItemProps } from '@/components/Menu';
import {
  canCancelJob,
  canRerunJob,
  hasJobArtifacts,
} from '@/sections/jobs/utils/jobStatus.util';
import { Job } from '@/types';

/**
 * Parameters for building job action menu options
 */
export interface GetJobActionOptionsParams {
  /** Job to display actions for */
  job: Job;
  /** Action handlers for job operations */
  handlers: {
    /** Rerun a completed or failed job */
    handleRerun: (jobKey: string) => Promise<any>;
    /** Cancel a running or queued job */
    handleCancel: (jobKey: string) => Promise<any>;
    /** Download job results artifact */
    handleDownload: (url: string, filename: string) => void;
  };
}

/**
 * Generate action menu options for a job row
 *
 * Creates a context-aware list of actions based on job status:
 * - Completed/failed jobs: Show rerun and download (if artifacts exist)
 * - Running/queued jobs: Show cancel
 *
 * @param params - Job and action handlers
 * @returns Array of menu item options for the action menu
 *
 * @example
 * ```tsx
 * const options = getJobActionOptions({
 *   job: myJob,
 *   handlers: {
 *     handleRerun: async (key) => { ... },
 *     handleCancel: async (key) => { ... },
 *     handleDownload: (url, filename) => { ... },
 *   },
 * });
 * ```
 */
export const getJobActionOptions = ({
  job,
  handlers,
}: GetJobActionOptionsParams): Omit<MenuItemProps, 'isFocused'>[] => {
  // Determine available actions using extracted utility functions
  const canRerun = canRerunJob(job);
  const canCancel = canCancelJob(job);
  const hasDownload = hasJobArtifacts(job);

  const options: Omit<MenuItemProps, 'isFocused'>[] = [];

  // Rerun action (available for completed jobs)
  if (canRerun) {
    options.push({
      label: 'Rerun Job',
      value: 'rerun',
      icon: <RotateLeftIcon className="size-4" />,
      onClick: () => handlers.handleRerun(job.key),
    });
  }

  // Download action (available when artifacts exist)
  if (hasDownload) {
    options.push({
      label: 'Download Results',
      value: 'download',
      icon: <DownloadIcon className="size-4" />,
      onClick: () =>
        handlers.handleDownload(
          job.s3DownloadURL!,
          job.large_artifact_filename!
        ),
    });
  }

  // Cancel action (available for running/queued jobs)
  if (canCancel) {
    options.push({
      label: 'Cancel Job',
      value: 'cancel',
      icon: <XMarkIcon className="size-4" />,
      onClick: () => handlers.handleCancel(job.key),
    });
  }

  return options;
};
